import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Services } from '@/services/services'

import './Main.css'
import { Translates } from '@/services/repository/i18n'
import { MainPageLayout } from '@/components/layout/MainLayout'
import { MachinePath } from '../../Common'
import { Machine } from '@/services/model/machine'
import { CurrentSession } from '@/services/repository/session'
import { CleanWizardData, LoadWizardData, SaveWizardData, WizardData } from './Model'
import { BackButton } from '@/components/layout/NavBar'
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition'

export const MachineFilesWizardStep2 = ({
  services,
  session
}: {
  services: Services
  session?: CurrentSession
}) => {
  const navigate = useNavigate()
  const machineId = useParams().machineid
  const [translates, setTranslates] = useState<Translates>()
  const [machine, setMachine] = useState<Machine>()
  const [error, setError] = useState<string>()
  const [wizardData, setWizardData] = useState<WizardData>()
  const [isListening, setIsListening] = useState(false)

  const { start, stop } = useSpeechRecognition({
    lang: session?.user.lang || services.i18n.defaultLang() || 'pl',
    baseText: wizardData?.description || '',
    onResult: (text) => {
      setWizardData((prev) => {
        if (!prev) return prev
        var data = { ...prev, description: text }
        SaveWizardData(machineId, data)
        return data
      })
    },
    onError: (err) => console.error('Speech error:', err)
  })

  const toggleListening = () => {
    if (!wizardData) return
    if (isListening) {
      stop()
    } else {
      start()
    }
    setIsListening((prev) => !prev)
  }

  useEffect(() => {
    if (session) {
      setTranslates(session.translates)
    } else {
      services.i18n.get('main').then(setTranslates)
    }
  }, [session])

  useEffect(() => {
    if (machineId) {
      fetch(`/api/communal/machines/byid/${machineId}`)
        .then((res) => res.json())
        .then(setMachine)
    }
  }, [machineId])

  useEffect(() => {
    if (machineId !== undefined) {
      setWizardData(LoadWizardData(machineId))
    }
  }, [machineId])

  if (
    !translates ||
    !machine ||
    !wizardData ||
    machineId === undefined
  ) {
    return <div className='spinner'></div>
  }

  return (
    <MainPageLayout
      pageName='machine-files-wizard'
      services={services}
      translates={translates}
      session={session}
      path={MachinePath({ session, translates, machine })}
      navbarLeft={
        <BackButton
          services={services}
          translates={translates}
          replaceUrl={`/communal/machine/${machineId}/files/upload/page1`}
        />
      }
    >
      <h2 className='page-title'>{translates.for(`wizards.machine_files.title`)}</h2>

      <div className='description-box'>
        <textarea
          value={wizardData.description}
          onChange={(e) =>
            setWizardData((prev) => {
              if(!prev) return
              var data = { ...prev, description: e.target.value }
              SaveWizardData(machineId, data)
              return data
            })
          }
          placeholder={translates.for(`wizards.machine_files.description_placeholder`)}
          readOnly={isListening}
        />
      </div>

      <div
        className={isListening ? 'record-btn pulse' : 'record-btn'}
        onClick={toggleListening}
      >
        {isListening
          ? translates.for(`wizards.machine_files.stop_recording`)
          : translates.for(`wizards.machine_files.record`)}
        <img
          src={`/public/img/icons/microphone.svg`}
          style={{ opacity: isListening ? 0.5 : 1 }}
        />
      </div>

      <div
        className='approve-btn'
        onClick={() => {
          services.session.fetchWithToken(`/api/communal/machines/byid/${machineId}/files/new`, {
            method: 'POST',
            body: JSON.stringify(wizardData),
          }).then(async (r) => {
            if (r.status !== 200) {
              const err = await r.text()
              console.log(err)
              setError(err)
              return
            }
            return navigate(`/communal/machine/${machineId}/files`)
          })
        }}>
        {translates.for(`wizards.machine_files.approve`)}
      </div>
      {error ? <div className='critic-error-box'>{translates.for('errors.general_error')}</div> : ''}
    </MainPageLayout>
  )
}
